import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { Flex } from '@resideo/blueprint-react';
import primaryNavigation from 'config/navigation';
import { resideoPerksLink } from 'config/externalLinks';
import Header from './Header';
import HeaderNavLink from './HeaderNavLink';
import MobileMenu, { MobileMenuToggle } from './MobileMenu';
import ProfileMenu from './ProfileMenu';
import Notifications from './Notifications';
import { graphql } from 'babel-plugin-relay/macro';
import { fetchQuery } from 'relay-runtime';
import { useRelayEnvironment } from 'relay-hooks';
import { useIsMounted } from 'hooks/useIsMounted';
import { AuthenticatedHeaderQuery } from './__generated__/AuthenticatedHeaderQuery.graphql';

interface PrimaryNavOuter {
  mobileMenuOpen: boolean;
}

const PrimaryNavOuter = styled.nav<PrimaryNavOuter>`
  display: flex;
  align-items: center;
  width: 100%;
  height: ${({ mobileMenuOpen }) => (mobileMenuOpen ? 'inherit' : '3.75rem')};
  background-color: ${({ theme }) => theme.colors.header.primaryBackground};
  overflow: hidden;
  @media (min-width: ${({ theme }) => theme.breakpoints.xLarge}) {
    height: 3.75rem;
  }
`;

const PrimaryNavLinksContainer = styled.div`
  display: none;
  margin-right: auto;
  @media (min-width: ${({ theme }) => theme.breakpoints.large}) {
    display: block;
  }
`;

const StyledProfileMenu = styled(ProfileMenu)`
  display: none;

  @media (min-width: ${({ theme }) => theme.breakpoints.large}) {
    display: block;
  }
`;

const useMountEffect = (cb: () => void) => useEffect(cb, []);

const AuthenticatedHeader: FC = () => {
  const isMounted = useIsMounted();
  const environment = useRelayEnvironment();
  const { pathname } = useLocation();

  const viewerQuery = graphql`
    query AuthenticatedHeaderQuery {
      viewer {
        partnerUsers {
          edges {
            node {
              ... on PartnerUser {
                partnerAccount {
                  ... on PartnerAccount {
                    id
                    partnerLoyaltyProfile {
                      loyaltyApprovalStatus
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  const [perksStatus, setPerksStatus] = useState<string>('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const fetchViewer = () => {
    if (environment === null) throw new Error('No relay environment set');

    fetchQuery<AuthenticatedHeaderQuery>(
      environment,
      viewerQuery,
      {}
    ).subscribe({
      next: response => {
        const theViewer = response?.viewer;
        const loyaltyStatus =
          theViewer?.partnerUsers?.edges?.[0]?.node?.partnerAccount
            ?.partnerLoyaltyProfile?.loyaltyApprovalStatus;
        if (isMounted()) {
          setPerksStatus(loyaltyStatus ?? '');
        }
      },
      error: () => null,
    });
  };

  useMountEffect(fetchViewer);

  const myBusinessActive = !(
    pathname.includes('pdp') || pathname.includes('select-plan')
  );

  return (
    <Header
      mobileMenuOpen={mobileMenuOpen}
      primaryNav={
        <>
          <PrimaryNavLinksContainer data-test-primary-web-nav>
            {primaryNavigation.map(data => (
              <HeaderNavLink
                key={data.name}
                linkPath={
                  data.name === 'perks'
                    ? perksStatus === 'APPROVED'
                      ? data.linkPath
                      : resideoPerksLink
                    : data.linkPath
                }
                name={data.name}
                linkTextKey={data.linkTextKey}
                isExternalLink={data.isExternalLink}
                isActive={data.name === 'mybusiness' && myBusinessActive}
              />
            ))}
          </PrimaryNavLinksContainer>
          <Flex>
            <Notifications />
            <StyledProfileMenu />
            <MobileMenuToggle
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            />
          </Flex>
          <MobileMenu
            show={mobileMenuOpen}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          />
        </>
      }
    />
  );
};

export default AuthenticatedHeader;
